import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'

import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  AppError,
  emitter,
  ErrorHandler,
  extractMessage,
  formatAddress,
} from '@/helpers'
import { convertAddressPrefix } from '@/helpers/cosmos'
import { buildUnjailMsg } from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

class UnjailError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Unjail failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'unjail',
    })
    this.name = 'UnjailError'
  }

  static fromUnknown(error: unknown): UnjailError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()
    if (lower.includes('not jailed')) {
      return new UnjailError({
        userMessage: 'Validator is not jailed',
        originalError: error,
      })
    }
    if (lower.includes('insufficient funds')) {
      return new UnjailError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }
    return new UnjailError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

function UnjailModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { signAndBroadcast, address } = useKeplrWallet()
  const queryClient = useQueryClient()

  const validatorAddress = address ? convertAddressPrefix(address) : null

  const unjailTx = useMutation({
    mutationKey: queryKeys.tx.unjail(validatorAddress ?? ''),
    mutationFn: async () => {
      if (!validatorAddress) {
        throw new Error('Validator address not found')
      }

      const msg = buildUnjailMsg({
        validatorAddress,
      })

      return signAndBroadcast([msg])
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Unjail transaction sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staking.validatorsIsUser(address),
      })
      onClose()
    },
    onError: error => {
      ErrorHandler.process(UnjailError.fromUnknown(error))
    },
  })

  const isLoading = unjailTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Unjail Validator</span>}
      content={
        <div className='text-foreground mt-4 flex flex-col gap-4 p-1'>
          <div className='bg-warning/10 border-warning flex items-start gap-3 rounded-lg border p-4'>
            <AlertCircle className='text-warning mt-0.5 size-5 shrink-0' />
            <div className='flex flex-col gap-2'>
              <p className='text-sm font-semibold'>Validator Jailed</p>
              <p className='text-muted-foreground text-xs leading-relaxed'>
                Your validator has been jailed due to downtime or
                double-signing. To restore your validator to active status and
                resume operations, you must send an unjail transaction.
              </p>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              Validator Address
            </span>
            <div className='bg-input text-foreground flex min-h-12 items-center rounded-lg px-3 py-2 text-sm break-all'>
              {validatorAddress || 'Not available'}
            </div>
          </div>

          <div className='bg-muted/30 rounded-lg p-4'>
            <p className='text-muted-foreground text-xs leading-relaxed'>
              After unjailing, ensure your validator infrastructure is running
              properly to avoid being jailed again.
            </p>
          </div>
        </div>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isLoading || !validatorAddress}
            onClick={() => unjailTx.mutate()}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Unjailing…
              </span>
            ) : (
              'Unjail Validator'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}

export default UnjailModal
